/**
 * Persistent CodeMirror host for the framework-free web SPA.
 *
 * `render()` / `updateRail()` rebuild the Files panel on every state change.
 * The kernel must live OUTSIDE that rebuild: same path keeps the view
 * (cursor, undo, scroll); a different path remounts; leaving the tab or
 * switching to Markdown preview destroys. Hosted web never wires language
 * servers, a PTY, SCM writes, or desktop-only AI apply-edit.
 *
 * Save snapshots the live document and never overwrites a newer draft after
 * the write. Disk refresh is generation-gated so a late older `fs.read` cannot
 * apply. Auth/project transitions go through `resetWebEditorState` so a
 * previous tenant's buffer cannot paint.
 */
import {
  languageFor,
  mountEditor,
  type EditorAppearance,
  type EditorKernel,
  type EditorSelection,
} from "@tavsin/editor";

export interface WebEditorCallbacks {
  onChange: (next: string) => void;
  onSave: () => void;
  onSelection?: (selection: EditorSelection) => void;
}

export interface AttachWebEditorInput {
  slot: HTMLElement;
  path: string;
  text: string;
  savedContent: string;
  appearance: EditorAppearance;
  readOnly?: boolean;
  /** Survives a remount (markdown preview destroys the kernel). */
  diskChanged?: boolean;
  callbacks: WebEditorCallbacks;
}

export interface WebEditorController {
  readonly path: string;
  readonly kernel: EditorKernel;
  readonly host: HTMLElement;
  readonly diskChanged: boolean;
  readonly languageId: string | null;
  getDocument(): string;
  getSelection(): EditorSelection;
  isDirty(): boolean;
  setAppearance(appearance: EditorAppearance): void;
  setReadOnly(readOnly: boolean): void;
  /** Record a written snapshot as the baseline. Does not touch a newer draft. */
  markSaved(savedContent: string): void;
  /** Hide the disk-changed banner; keep the live draft and baseline. */
  dismissDiskChanged(): void;
  /**
   * Agent / disk refresh. Clean → replace the doc. Dirty → keep the buffer
   * and report that disk moved, so the host can show a banner. Rechecks
   * immediately before `setDocument` so a dirty buffer cannot slip through.
   */
  syncFromDisk(diskContent: string): "updated" | "preserved";
  /** Explicit Reload from disk (caller confirmed if dirty). */
  applyFromDisk(diskContent: string): void;
  attach(slot: HTMLElement): void;
  destroy(): void;
}

/** The SPA fields the editor helpers mutate. Extra fields on `state` are fine. */
export interface WebFileEditorState {
  saving: boolean;
  openFile: { path: string; content: string } | null;
  editBuffer: string | null;
  openDiff: { path: string; diff: string } | null;
  filePreview: boolean;
  fileDiskChanged: boolean;
}

export type SaveOpenFileResult =
  | { kind: "skipped" }
  | { kind: "written"; path: string; snapshot: string }
  | { kind: "failed"; error: string };

export interface DiskRefreshToken {
  seq: number;
  path: string;
}

interface Session {
  kernel: EditorKernel;
  host: HTMLElement;
  path: string;
  savedContent: string;
  diskChanged: boolean;
  box: { callbacks: WebEditorCallbacks };
  lastSelection: EditorSelection;
}

let session: Session | null = null;

/** Monotonic generation: only the latest `fs.read` for the current path may apply. */
let refreshSeq = 0;
let refreshPath = "";

/** Honest label: a real parser id, or "text" — never a stand-in language. */
export function webLanguageLabel(path: string): string {
  return languageFor(path).languageId ?? "text";
}

export function webEditorAriaLabel(path: string): string {
  return `Editor ${path} (${webLanguageLabel(path)})`;
}

export function webEditorSelection(): EditorSelection | null {
  if (!session) return null;
  return session.lastSelection;
}

export function currentWebEditor(): WebEditorController | null {
  return session ? controllerFor(session) : null;
}

export function destroyWebEditor(): void {
  if (!session) return;
  session.kernel.destroy();
  session.host.remove();
  session = null;
}

/**
 * Auth / boot / project boundary. Destroys the kernel and clears every
 * editor field so a previous tenant's buffer cannot paint after the
 * transition. Safe to call twice.
 */
export function resetWebEditorState(state: WebFileEditorState): void {
  destroyWebEditor();
  invalidateDiskRefresh();
  state.openFile = null;
  state.editBuffer = null;
  state.openDiff = null;
  state.filePreview = false;
  state.fileDiskChanged = false;
  state.saving = false;
}

export function beginDiskRefresh(path: string): DiskRefreshToken {
  refreshSeq += 1;
  refreshPath = path;
  return { seq: refreshSeq, path };
}

/** Bump generation so in-flight reads (including a re-opened same path) cannot apply. */
export function invalidateDiskRefresh(): void {
  refreshSeq += 1;
  refreshPath = "";
}

export function isLatestDiskRefresh(
  token: DiskRefreshToken,
  currentPath: string | null | undefined,
): boolean {
  return (
    token.seq === refreshSeq &&
    token.path === refreshPath &&
    currentPath === token.path
  );
}

/** Live document when the kernel is on this path; otherwise the SPA buffer. */
export function snapshotForSave(path: string, fallback: string): string {
  const editor = currentWebEditor();
  if (editor && editor.path === path) return editor.getDocument();
  return fallback;
}

/**
 * One in-flight write. Snapshots the live doc, writes that snapshot, then
 * `markSaved(snapshot)` so a draft typed during `await` stays dirty.
 */
export async function saveOpenFile(
  state: WebFileEditorState,
  write: (path: string, content: string) => Promise<void>,
  hooks?: { onSaving?: () => void },
): Promise<SaveOpenFileResult> {
  if (state.saving) return { kind: "skipped" };
  if (!state.openFile || state.editBuffer === null) return { kind: "skipped" };
  const path = state.openFile.path;
  const snapshot = snapshotForSave(path, state.editBuffer);
  if (snapshot === state.openFile.content) return { kind: "skipped" };
  state.editBuffer = snapshot;
  state.saving = true;
  hooks?.onSaving?.();
  try {
    await write(path, snapshot);
    if (state.openFile?.path === path) {
      state.openFile = { path, content: snapshot };
      state.fileDiskChanged = false;
      const editor = currentWebEditor();
      if (editor && editor.path === path) editor.markSaved(snapshot);
    }
    return { kind: "written", path, snapshot };
  } catch (e) {
    return { kind: "failed", error: e instanceof Error ? e.message : String(e) };
  } finally {
    state.saving = false;
  }
}

/**
 * Apply a completed `fs.read` only when it is still the latest generation
 * for the file that is open. Never clobbers a dirty buffer.
 */
export function applyDiskRead(
  state: WebFileEditorState,
  token: DiskRefreshToken,
  disk: { path: string; content: string },
): "updated" | "preserved" | "ignored" {
  if (!isLatestDiskRefresh(token, state.openFile?.path ?? null)) return "ignored";
  if (state.openFile?.path !== disk.path) return "ignored";

  const editor = currentWebEditor();
  if (editor && editor.path === disk.path) {
    const result = editor.syncFromDisk(disk.content);
    if (result === "updated") {
      state.openFile = disk;
      state.editBuffer = disk.content;
      state.fileDiskChanged = false;
    } else {
      state.editBuffer = editor.getDocument();
      state.fileDiskChanged = editor.diskChanged;
    }
    return result;
  }

  const baseline = state.openFile.content;
  const live = (): string => state.editBuffer ?? baseline;
  if (live() !== baseline) {
    state.fileDiskChanged = disk.content !== baseline;
    return "preserved";
  }
  if (live() !== baseline) {
    state.fileDiskChanged = disk.content !== baseline;
    return "preserved";
  }
  state.openFile = disk;
  state.editBuffer = disk.content;
  state.fileDiskChanged = false;
  return "updated";
}

export function attachWebEditor(input: AttachWebEditorInput): WebEditorController {
  const { slot, path, text, savedContent, appearance, callbacks } = input;
  const readOnly = input.readOnly === true;
  const diskChanged = input.diskChanged === true;

  if (session && session.path === path) {
    session.box.callbacks = callbacks;
    adoptSavedBaseline(session, savedContent);
    if (diskChanged) session.diskChanged = true;
    session.kernel.setAppearance(appearance);
    session.kernel.setReadOnly(readOnly);
    attachHost(session.host, slot);
    return controllerFor(session);
  }

  destroyWebEditor();

  const host = globalThis.document.createElement("div");
  host.className = "cm-host";
  host.setAttribute("aria-label", webEditorAriaLabel(path));
  const box: Session["box"] = { callbacks };
  const kernel = mountEditor(host, {
    path,
    document: text,
    appearance,
    ...(readOnly ? { readOnly: true } : {}),
    onChange: (next) => box.callbacks.onChange(next),
    onSave: () => box.callbacks.onSave(),
    onSelection: (selection) => {
      if (session) session.lastSelection = selection;
      box.callbacks.onSelection?.(selection);
    },
  });
  session = {
    kernel,
    host,
    path,
    savedContent,
    diskChanged,
    box,
    lastSelection: kernel.getSelection(),
  };
  attachHost(host, slot);
  return controllerFor(session);
}

/**
 * Same-path rerender: never replace the live doc. Update the saved baseline
 * only when the buffer is clean and the incoming baseline matches it, or
 * when the parent is explicitly restating the current baseline. A dirty
 * draft keeps its baseline and diskChanged flag — a stale or new parent
 * `file.content` must not make the draft look saved.
 */
function adoptSavedBaseline(s: Session, incoming: string): void {
  const current = s.kernel.view.state.doc.toString();
  const dirty = current !== s.savedContent;
  if (!dirty) {
    if (incoming === current) s.savedContent = incoming;
    return;
  }
  if (incoming === s.savedContent) s.savedContent = incoming;
}

function attachHost(host: HTMLElement, slot: HTMLElement): void {
  if (host.parentElement !== slot) slot.append(host);
  session?.kernel.view.requestMeasure();
}

function controllerFor(s: Session): WebEditorController {
  return {
    get path(): string {
      return s.path;
    },
    get kernel(): EditorKernel {
      return s.kernel;
    },
    get host(): HTMLElement {
      return s.host;
    },
    get diskChanged(): boolean {
      return s.diskChanged;
    },
    get languageId(): string | null {
      return s.kernel.languageId;
    },
    getDocument(): string {
      return s.kernel.view.state.doc.toString();
    },
    getSelection(): EditorSelection {
      return s.lastSelection;
    },
    isDirty(): boolean {
      return s.kernel.view.state.doc.toString() !== s.savedContent;
    },
    setAppearance(appearance: EditorAppearance): void {
      s.kernel.setAppearance(appearance);
    },
    setReadOnly(readOnly: boolean): void {
      s.kernel.setReadOnly(readOnly);
    },
    markSaved(next: string): void {
      s.savedContent = next;
      s.diskChanged = false;
    },
    dismissDiskChanged(): void {
      s.diskChanged = false;
    },
    syncFromDisk(diskContent: string): "updated" | "preserved" {
      const live = (): string => s.kernel.view.state.doc.toString();
      if (live() !== s.savedContent) {
        s.diskChanged = diskContent !== s.savedContent;
        return "preserved";
      }
      // Recheck immediately before replacing. This function is synchronous
      // so a dirty buffer cannot land between the two reads.
      if (live() !== s.savedContent) {
        s.diskChanged = diskContent !== s.savedContent;
        return "preserved";
      }
      s.kernel.setDocument(diskContent);
      s.savedContent = diskContent;
      s.diskChanged = false;
      return "updated";
    },
    applyFromDisk(diskContent: string): void {
      s.kernel.setDocument(diskContent);
      s.savedContent = diskContent;
      s.diskChanged = false;
    },
    attach(slot: HTMLElement): void {
      attachHost(s.host, slot);
    },
    destroy(): void {
      if (session === s) destroyWebEditor();
    },
  };
}
